import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LayoutDashboard } from "lucide-react";
import { NAV, IMAGES } from "../data/content";
import { useAuth } from "../context/AuthContext";
import { startGoogleLogin } from "../lib/api";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-cream/90 backdrop-blur-md border-b border-navy/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" className="flex flex-col items-center leading-none group" data-testid="nav-logo">
          <img
            src={IMAGES.tree}
            alt="Officium Mentale"
            className="h-11 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
          />
          <span className="font-serif text-navy text-[0.7rem] tracking-[0.12em] mt-0.5 whitespace-nowrap">Officium Mentale</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-7 whitespace-nowrap">
          {NAV.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              title={item.hint}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`link-underline flex flex-col text-[13px] xl:text-sm font-medium tracking-wide transition-colors ${
                pathname === item.path ? "text-navy" : "text-navy/60 hover:text-navy"
              }`}
            >
              <span>{item.label}</span>
              {item.hint && (
                <span className="hidden xl:block text-[10px] font-normal uppercase tracking-wider text-navy/45 leading-none mt-1" data-testid={`nav-hint-${item.path.slice(1)}`}>
                  {item.hint}
                </span>
              )}
            </Link>
          ))}
        </nav>

        {user ? (
          <Link to="/dashboard" data-testid="nav-dashboard" className="hidden lg:inline-flex items-center gap-2 rounded-full bg-navy text-cream pl-2 pr-5 py-1.5 text-sm font-semibold hover:bg-navy-light transition-colors">
            {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" /> : <LayoutDashboard size={16} className="ml-2" />}
            Dashboard
          </Link>
        ) : (
          <button onClick={startGoogleLogin} data-testid="nav-signin" title="Sign in to your lessons, reflections, and certificates" className="hidden lg:inline-flex items-center rounded-full bg-navy text-cream px-4 xl:px-5 py-2.5 text-[13px] xl:text-sm font-semibold hover:bg-navy-light transition-colors whitespace-nowrap">
            Learner sign in
          </button>
        )}

        <button
          className="lg:hidden text-navy p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-cream border-t border-navy/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-serif text-2xl text-navy"
                >
                  {item.label}
                  {item.hint && <span className="block font-sans text-sm text-navy/50 mt-0.5">{item.hint}</span>}
                </Link>
              ))}
              <Link to="/faq" data-testid="nav-mobile-faq" className="font-serif text-2xl text-navy">FAQ</Link>
              {user ? (
                <Link to="/dashboard" data-testid="nav-mobile-dashboard" className="font-serif text-2xl text-gold">Dashboard</Link>
              ) : (
                <button onClick={startGoogleLogin} data-testid="nav-mobile-signin" className="mt-2 self-start inline-flex items-center rounded-full bg-navy text-cream px-6 py-3 font-semibold">Learner sign in</button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
